import { Card, CardMainAction } from '@plato/ui/containers/card';
import { useEffect, useEffectEvent, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { InsightCardFront } from '../../insights-common/components-new/insight-card-front.tsx';
import type { SendAnalyticsEventHandler } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { CustomerDetailInsightDialogs } from './customer-detail-insight-dialogs.tsx';

export type CustomerDetailInsightCardProps = {
    insightData: InsightListItem;
    onAnalyticsEvent: SendAnalyticsEventHandler;
    onVisibleChanged?: (isVisible: boolean) => void;
};

export function CustomerDetailInsightCard({
    insightData,
    onAnalyticsEvent,
    onVisibleChanged,
}: CustomerDetailInsightCardProps): ReactElement {
    const { t } = useTranslation();

    const { insight } = insightData;

    const [isDetailOpen, setIsDetailOpen] = useState(false);

    const openDetail = (): void => {
        setIsDetailOpen(true);
        onAnalyticsEvent({
            event: 'sales_insight_expanded',
            payload: { sales_insight_id: insight.id },
        });
    };

    const { ref, inView } = useInView({
        onChange: (isVisible) => {
            onVisibleChanged?.(isVisible);
        },
        threshold: 0.8, // 80% of the component should be visible (to account for padding)
    });

    const sendViewed = useEffectEvent((insightId: string) => {
        onAnalyticsEvent({
            event: 'sales_insight_viewed',
            payload: { sales_insight_id: insightId },
        });
    });

    useEffect(() => {
        if (!inView) {
            return;
        }
        const handle = setTimeout(sendViewed, 3000, insight.id);
        return () => {
            clearTimeout(handle);
        };
    }, [inView, insight.id]);

    return (
        <div ref={ref}>
            <CustomerDetailInsightDialogs
                insightData={insightData}
                open={isDetailOpen}
                onClose={() => {
                    setIsDetailOpen(false);
                }}
                onAnalyticsEvent={onAnalyticsEvent}
            />
            <Card role="article">
                <InsightCardFront insightData={insightData}>
                    <CardMainAction aria-label={t('show_details', { ns: 'common' })} onClick={openDetail} />
                </InsightCardFront>
            </Card>
        </div>
    );
}
